import {
  useCallback, useEffect, useMemo, useState,
} from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../modules/auth/useAuth";
import { Button, Modal } from "@xfrontend/xui-react-plus";
import { toast } from "#/infra/Toastify";
import { useOrg } from "#/modules/auth/OrgContext";
import { Rbac } from "#/modules/auth/useRbac";
import {
  UserItem, listUsers, disableUser,
} from "#/apis/infra/users";
import listCustomers, { CustomerItem } from "#/apis/infra/customers";
import UserTable from "./UserTable";
import UserFormModal from "./UserFormModal";
import AssignmentModal from "./AssignmentModal";
import OrgEditModal from "./OrgEditModal";

const Toolbar = styled.div`
  display: flex; justify-content: flex-end; margin: 0 0 12px;
`;

const UsersTab = ({ rbac }: { rbac: Rbac }): JSX.Element => {
  const { t } = useTranslation("management");
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const { orgs, refreshOrgs } = useOrg();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [customers, setCustomers] = useState<CustomerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  // Org card deep-links (full reload, so these are read once on mount): ?license= pre-filters the
  // Scope column; ?edit=1 opens the org editor for that org. SYSTEM_ADMIN sees all users globally;
  // the Scope column filter (client-side) narrows the loaded list — no separate scope bar.
  const deepLinkLicenseId = searchParams.get("license") ?? "";
  const [showEditOrg, setShowEditOrg] = useState(() => searchParams.get("edit") === "1");
  // Hold the id, not a snapshot — so the open AssignmentModal re-derives the user from the
  // refetched `users` and its "current roles" list stays live after add/remove.
  const [assignUserId, setAssignUserId] = useState<string | null>(null);
  const [disableTarget, setDisableTarget] = useState<UserItem | null>(null);

  const token = auth.user?.access_token;

  const load = useCallback((): void => {
    if (!token) return;
    setLoading(true);
    Promise.all([listUsers({ token }), listCustomers({ token })]).then(([u, c]) => {
      if (u.success && u.data) setUsers(u.data.items);
      if (c.success && c.data) setCustomers(c.data.items);
      setLoading(false);
    });
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Resolve a scope_id to a human name (Organization / Territory), falling back to the raw id.
  const scopeName = useMemo(() => {
    const lic = new Map(orgs.map((o) => [o.license_id, o.license_name]));
    const cus = new Map(customers.map((c) => [c.customer_id, c.customer_name]));
    return (scopeType: string, scopeId: string): string => {
      if (scopeType === "SYSTEM") return "SYSTEM";
      if (scopeType === "LICENSE") return lic.get(scopeId) ?? scopeId;
      if (scopeType === "CUSTOMER") return cus.get(scopeId) ?? scopeId;
      return scopeId;
    };
  }, [orgs, customers]);

  const confirmDisable = (): void => {
    if (!token || !disableTarget) return;
    disableUser({ token, userId: disableTarget.user_id }).then((res) => {
      setDisableTarget(null);
      if (res.success) {
        toast.success(t("toast.disabled"));
        load();
      } else {
        toast.error(res.msg || t("toast.failed"));
      }
    });
  };

  const editOrg = orgs.find((o) => o.license_id === deepLinkLicenseId) ?? null;

  return (
    <div>
      {rbac.canCreateUser && (
        <Toolbar>
          <Button variant="primary" onClick={() => setShowCreate(true)}>{t("users.create")}</Button>
        </Toolbar>
      )}

      <UserTable
        users={users}
        loading={loading}
        rbac={rbac}
        orgs={orgs}
        customers={customers}
        scopeName={scopeName}
        initialOrgId={deepLinkLicenseId}
        onManageRoles={(u) => setAssignUserId(u.user_id)}
        onDisable={setDisableTarget}
      />

      <UserFormModal
        show={showCreate}
        orgs={orgs}
        customers={customers}
        onClose={() => setShowCreate(false)}
        onCreated={load}
      />
      <AssignmentModal
        show={!!assignUserId}
        user={users.find((u) => u.user_id === assignUserId) ?? null}
        orgs={orgs}
        customers={customers}
        scopeName={scopeName}
        isSelf={!!assignUserId && rbac.isSelf(assignUserId)}
        onClose={() => setAssignUserId(null)}
        onChanged={load}
      />

      <OrgEditModal
        show={showEditOrg && rbac.canEditOrg}
        org={editOrg}
        onClose={() => setShowEditOrg(false)}
        onSaved={refreshOrgs}
      />

      <Modal
        title={t("users.disable")}
        show={!!disableTarget}
        onClose={() => setDisableTarget(null)}
        showCloseBtn
        restoreFocus={false}
      >
        <Modal.Body>{t("confirm.disable")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDisableTarget(null)}>{t("form.cancel")}</Button>
          <Button variant="primary" onClick={confirmDisable}>{t("users.disable")}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UsersTab;
